<template>
  <div class="privacy-policy-editor">
    <div class="c-responsive-fixwidth">
      <div class="o-main-content-area">
        <div class="col-md-12 o-no-padding c-common-box-shadow u-bg-color-fff" style="padding: 20px !important;">

          <!-- Header -->
          <div class="row" style="margin-bottom: 15px;">
            <div class="col-md-6">
              <h3>{{ clientName }} CS Privacy Policy</h3>
            </div>
            <div class="col-md-6 text-right">
              <button v-if="!editing" class="c-solid-btn c-solid-btn--green" @click="startNew">
                <i class="fa fa-plus" aria-hidden="true"></i> New Policy
              </button>
            </div>
          </div>

          <!-- Policy List (when not editing) -->
          <div v-if="!editing">
            <div v-if="policies.length === 0" class="text-center" style="padding: 40px; color: #999;">
              No privacy policies yet. Click "New Policy" to create one.
            </div>
            <div v-for="policy in policies" :key="policy.id" class="policy-card" :class="{ 'policy-inactive': !policy.is_active }">
              <div class="row">
                <div class="col-md-6">
                  <strong>{{ policy.title }}</strong>
                  <span class="policy-version">v{{ policy.version }}</span>
                  <span v-if="!policy.is_active" class="policy-badge policy-badge--inactive">Inactive</span>
                  <span v-else class="policy-badge policy-badge--active">Active</span>
                  <br>
                  <small style="color: #999;">
                    Target: {{ formatTargetType(policy.target_type) }}
                  </small>
                </div>
                <div class="col-md-6 text-right">
                  <button class="c-common-btn c-common-btn--small" @click="editPolicy(policy)">
                    <i class="fa fa-pencil"></i> Edit
                  </button>
                  <button class="c-common-btn c-common-btn--small" @click="togglePolicy(policy)">
                    <i class="fa" :class="policy.is_active ? 'fa-eye-slash' : 'fa-eye'"></i>
                    {{ policy.is_active ? 'Deactivate' : 'Activate' }}
                  </button>
                  <button class="c-common-btn c-common-btn--small" style="color: #EE6363;" @click="deletePolicy(policy)">
                    <i class="fa fa-trash"></i> Delete
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Editor Form (when editing) -->
          <div v-if="editing">
            <div class="row" style="margin-bottom: 15px;">
              <div class="col-md-8">
                <label>Policy Title:</label>
                <input type="text" class="form-control" v-model="form.title" placeholder="e.g. CS Privacy Policy">
              </div>
              <div class="col-md-4 text-right" style="padding-top: 22px;">
                <button class="c-solid-btn c-solid-btn--green" @click="savePolicy" :disabled="saving">
                  <i class="fa fa-floppy-o" aria-hidden="true"></i>
                  {{ form.id ? 'Save (new version)' : 'Create' }}
                </button>
                <button class="c-common-btn" @click="cancelEdit" style="margin-left: 5px;">
                  Cancel
                </button>
              </div>
            </div>

            <!-- Targeting -->
            <div class="row" style="margin-bottom: 15px;">
              <div class="col-md-12">
                <label>Show this policy to:</label>
                <el-radio-group v-model="form.target_type" @change="onTargetTypeChange" style="margin-left: 10px;">
                  <el-radio label="all">All Users</el-radio>
                  <el-radio label="role">By Role</el-radio>
                  <el-radio label="skill">By Skill</el-radio>
                  <el-radio label="employee">By Employee</el-radio>
                </el-radio-group>
              </div>
            </div>

            <!-- Role selector -->
            <div v-if="form.target_type === 'role'" class="row" style="margin-bottom: 15px;">
              <div class="col-md-12">
                <label>Select Roles:</label>
                <el-select v-model="form.target_ids" multiple placeholder="Select roles..." style="width: 100%;">
                  <el-option v-for="r in availableRoles" :key="r.id" :label="r.label" :value="r.id"></el-option>
                </el-select>
              </div>
            </div>

            <!-- Skill selector -->
            <div v-if="form.target_type === 'skill'" class="row" style="margin-bottom: 15px;">
              <div class="col-md-12">
                <label>Select Skills:</label>
                <el-select v-model="form.target_ids" multiple filterable placeholder="Select skills..." style="width: 100%;">
                  <el-option v-for="s in availableSkills" :key="s.id" :label="s.title" :value="s.id"></el-option>
                </el-select>
              </div>
            </div>

            <!-- Employee selector -->
            <div v-if="form.target_type === 'employee'" class="row" style="margin-bottom: 15px;">
              <div class="col-md-12">
                <label>Select Employees:</label>
                <el-select v-model="form.target_ids" multiple filterable placeholder="Search employees..." style="width: 100%;">
                  <el-option v-for="e in availableEmployees" :key="e.id" :label="e.first_name + ' ' + e.last_name" :value="e.id"></el-option>
                </el-select>
              </div>
            </div>

            <!-- CKEditor -->
            <div class="row">
              <div class="col-md-12">
                <textarea id="privacy-policy-editor" name="privacy-policy-editor" style="width: 100%; min-height: 300px;"></textarea>
              </div>
            </div>

            <!-- Preview -->
            <div class="row" style="margin-top: 20px;">
              <div class="col-md-12">
                <h5 style="margin-bottom: 10px;">Preview (as seen by users):</h5>
                <div class="policy-preview" v-html="previewHtml"></div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import service from '../services.js';

export default {
  name: 'PrivacyPolicyEditor',
  data() {
    return {
      policies: [],
      editing: false,
      saving: false,
      editorInstance: null,
      previewHtml: '',
      clientName: '',
      availableRoles: [
        { id: '69714702-1497-4609-a57b-96e5618c534c', label: 'Client Admin' },
        { id: 'a28f89d3-96b3-4ee7-9f77-7fdee72cd839', label: 'Employee' },
        { id: '9d465974-900b-4437-bf4f-f5a4be53b57d', label: 'Power User' },
        { id: '06f94fd7-47db-4686-ba3a-4a0bfcac38d6', label: 'Auditor' },
      ],
      availableSkills: [],
      availableEmployees: [],
      form: {
        id: null,
        title: '',
        content: '',
        target_type: 'all',
        target_ids: [],
      }
    };
  },
  computed: {
    ...mapGetters({
      userToken: 'userToken',
      userRole: 'userRole',
      userClientID: 'userClientID',
      employeeId: 'employeeId',
      userRoleSlug: 'userRoleSlug'
    })
  },
  methods: {
    async loadPolicies() {
      try {
        var res = await service.get('client/' + this.userClientID + '/privacyPolicies', {
          'Authorization': this.userToken
        });
        this.policies = res.data.result || [];
      } catch (err) {
        console.log('loadPolicies error:', err);
      }
    },

    async loadClient() {
      try {
        var res = await service.get('client/' + this.userClientID, {
          'Authorization': this.userToken
        });
        this.clientName = res.data.result.name || res.data.result.subdomain || '';

        if (res.data.result.settings && res.data.result.settings['privacy-policy-editors']) {
          this.selectedEditors = res.data.result.settings['privacy-policy-editors'];
        }
      } catch (err) {
        console.log('loadClient error:', err);
      }
    },

    async loadSkills() {
      try {
        var res = await service.get('client/' + this.userClientID + '/skill', {
          'Authorization': this.userToken
        });
        this.availableSkills = res.data.result || [];
      } catch (err) {
        console.log('loadSkills error:', err);
      }
    },

    async loadEmployees() {
      try {
        var res = await service.get('client/' + this.userClientID + '/employee', {
          'Authorization': this.userToken
        });
        this.availableEmployees = res.data.result || [];
      } catch (err) {
        console.log('loadEmployees error:', err);
      }
    },

    startNew() {
      this.form = {
        id: null,
        title: this.clientName + ' CS Privacy Policy',
        content: '',
        target_type: 'all',
        target_ids: [],
      };
      this.previewHtml = '';
      this.editing = true;
      this.$nextTick(function () { this.initEditor(); }.bind(this));
    },

    editPolicy(policy) {
      this.form = {
        id: policy.id,
        title: policy.title,
        content: policy.content || '',
        target_type: policy.target_type,
        target_ids: policy.target_ids || [],
      };
      this.previewHtml = policy.content || '';
      this.editing = true;
      this.$nextTick(function () { this.initEditor(); }.bind(this));
    },

    cancelEdit() {
      this.destroyEditor();
      this.editing = false;
    },

    onTargetTypeChange() {
      this.form.target_ids = [];
    },

    formatTargetType(type) {
      var labels = { all: 'All Users', role: 'By Role', skill: 'By Skill', employee: 'By Employee' };
      return labels[type] || type;
    },

    async savePolicy() {
      if (!this.editorInstance) return;
      var self = this;
      self.saving = true;
      var html = self.editorInstance.getData();
      self.form.content = html;

      try {
        if (self.form.id) {
          await service.put('client/' + self.userClientID + '/privacyPolicy/' + self.form.id, {
            title: self.form.title,
            content: self.form.content,
            target_type: self.form.target_type,
            target_ids: self.form.target_ids,
          }, {
            'Authorization': self.userToken
          });
          self.$notify.success({ title: 'Success', message: 'Privacy policy updated (new version)' });
        } else {
          await service.post('client/' + self.userClientID + '/privacyPolicy', {
            title: self.form.title,
            content: self.form.content,
            target_type: self.form.target_type,
            target_ids: self.form.target_ids,
          }, {
            'Authorization': self.userToken
          });
          self.$notify.success({ title: 'Success', message: 'Privacy policy created' });
        }

        self.destroyEditor();
        self.editing = false;
        await self.loadPolicies();
      } catch (err) {
        console.log('savePolicy error:', err);
        self.$notify.error({ title: 'Error', message: 'Failed to save privacy policy' });
      }
      self.saving = false;
    },

    async togglePolicy(policy) {
      try {
        await service.post('client/' + this.userClientID + '/privacyPolicy/' + policy.id + '/toggle', {}, {
          'Authorization': this.userToken
        });
        await this.loadPolicies();
      } catch (err) {
        console.log('togglePolicy error:', err);
      }
    },

    async deletePolicy(policy) {
      if (!confirm('Are you sure you want to delete this privacy policy?')) return;
      try {
        await service.del('client/' + this.userClientID + '/privacyPolicy/' + policy.id, {
          'Authorization': this.userToken
        });
        await this.loadPolicies();
        this.$notify.success({ title: 'Success', message: 'Privacy policy deleted' });
      } catch (err) {
        console.log('deletePolicy error:', err);
      }
    },

    initEditor() {
      var self = this;
      setTimeout(function () {
        self.editorInstance = CKEDITOR.replace('privacy-policy-editor', {
          height: '45vh',
          linkDefaultProtocol: 'https://',
          linkShowTargetTab: true,
          contentsCss: [CKEDITOR.basePath + 'contents.css', 'body { color: #ffffff; background: #335479; }'],
          on: {
            instanceReady: function (evt) {
              evt.editor.on('dialogShow', function (e) {
                var dialog = e.data;
                if (dialog.getName() === 'link') {
                  var targetTab = dialog.getContentElement('target', 'linkTargetType');
                  if (targetTab && !dialog.getValueOf('target', 'linkTargetType')) {
                    dialog.setValueOf('target', 'linkTargetType', '_blank');
                  }
                }
              });

              evt.editor.on('change', function () {
                self.previewHtml = evt.editor.getData();
              });

              if (self.form.content) {
                evt.editor.setData(self.form.content);
              }
            }
          }
        });
      }, 500);
    },

    destroyEditor() {
      if (this.editorInstance) {
        try { this.editorInstance.destroy(); } catch (e) { /* noop */ }
        this.editorInstance = null;
      }
    }
  },

  async created() {
    await this.loadClient();
    await this.loadPolicies();
    this.loadSkills();
    this.loadEmployees();
  },

  beforeDestroy() {
    this.destroyEditor();
  }
};
</script>

<style scoped>
.policy-card {
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 15px;
  margin-bottom: 10px;
  background: #fff;
}
.policy-card:hover {
  background: #f9f9f9;
}
.policy-inactive {
  opacity: 0.6;
}
.policy-version {
  color: #999;
  font-size: 12px;
  margin-left: 8px;
}
.policy-badge {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 10px;
  margin-left: 8px;
}
.policy-badge--active {
  background: #1AB677;
  color: #fff;
}
.policy-badge--inactive {
  background: #999;
  color: #fff;
}
.policy-preview {
  background: #335479;
  color: #fff;
  border-radius: 4px;
  padding: 20px;
  min-height: 80px;
  border: 1px solid #466D98;
}
.policy-preview a {
  color: #5dade2;
}
</style>
